import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { studioActions, sel, toColor, CM_TO_PX } from '../../store/studioStore';

// ── Color swatch ──────────────────────────────────────────────────────────────
function ColorPick({ label, value, onChange, allowTransparent }) {
  // Handle transparent values properly
  const isTransparent = value === 'transparent';
  const color = isTransparent ? '#ffffff' : toColor(value) || '#ffffff';
  const displayValue = isTransparent ? 'TRANSPARENT' : color.toUpperCase();

  const inputRef = React.useRef();
  return (
    <div>
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <div className="sp-color-pick" onClick={() => inputRef.current.click()}>
          <div
            className="sp-color-swatch"
            style={{
              background: isTransparent
                ? 'repeating-conic-gradient(#ccc 0% 25%, transparent 0% 50%) 50% / 8px 8px'
                : color,
            }}
          />
          <span className="sp-color-hex">{displayValue}</span>
          <input
            ref={inputRef}
            type="color"
            value={color}
            onChange={(e) => onChange(e.target.value)}
            style={{
              position: 'absolute',
              opacity: 0,
              width: 0,
              height: 0,
              pointerEvents: 'none',
            }}
          />
        </div>
        {allowTransparent && (
          <button
            className="sp-btn"
            style={{
              padding: '4px 8px',
              fontSize: '11px',
              whiteSpace: 'nowrap',
              background: isTransparent ? 'var(--sp-orange)' : 'transparent',
              color: isTransparent ? '#fff' : 'inherit',
              border: '1px solid var(--sp-line)',
            }}
            onClick={() => onChange('transparent')}
            title="Rendre transparent"
          >
            ∅
          </button>
        )}
      </div>
    </div>
  );
}

ColorPick.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  allowTransparent: PropTypes.bool,
};

// ── Props field ───────────────────────────────────────────────────────────────
function Field({ label, children }) {
  return (
    <div>
      <label>{label}</label>
      {children}
    </div>
  );
}

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

export default function RightPanel({ scheduleSave }) {
  const dispatch = useDispatch();
  const selected = useSelector(sel.selectedElements);
  const doc = useSelector(sel.doc);
  const db = useSelector(sel.db);

  console.log('RightPanel render', { selected, doc, db });

  const it = selected.length === 1 ? selected[0] : null;

  const update = useCallback(
    (changes) => {
      if (!it) return;
      dispatch(studioActions.updateElement({ id: it.id, changes }));
      scheduleSave();
    },
    [it, dispatch, scheduleSave]
  );

  const num = (val) => (isNaN(Number(val)) ? 0 : Number(val));

  // Helper to safely get number value, avoiding NaN
  const safeNum = (val, fallback = 0) => {
    const parsed = Number(val);
    return isNaN(parsed) ? fallback : parsed;
  };

  // ── Document dims ─────────────────────────────────────────────────────────
  const wCm = doc?.widthPx
    ? Math.round((doc.widthPx / CM_TO_PX) * 10) / 10
    : 21.0;
  const hCm = doc?.heightPx
    ? Math.round((doc.heightPx / CM_TO_PX) * 10) / 10
    : 29.7;

  return (
    <section className="sp-right">
      {/* DB navigation */}
      {db?.fields &&
        db.fields.length > 0 &&
        db?.records &&
        db.records.length > 0 && (
          <div className="sp-db-nav">
            <button
              className="sp-btn"
              disabled={db.currentRecord === 0}
              onClick={() =>
                dispatch(studioActions.dbSetCurrentRecord(db.currentRecord - 1))
              }
            >
              ←
            </button>
            <span className="sp-db-nav-label">
              Enregistrement {db.currentRecord + 1} / {db.records.length}
            </span>
            <button
              className="sp-btn"
              disabled={db.currentRecord === db.records.length - 1}
              onClick={() =>
                dispatch(studioActions.dbSetCurrentRecord(db.currentRecord + 1))
              }
            >
              →
            </button>
          </div>
        )}

      {/* Alignment — only when multi-select */}
      {selected.length >= 2 && (
        <div className="sp-section">
          <h3>
            Alignement{' '}
            <span className="sp-tiny">({selected.length} éléments)</span>
          </h3>
          <div className="sp-tools-grid">
            {[
              ['left', '⯇ Gauche'],
              ['center', '⇔ Centre'],
              ['right', '⯈ Droite'],
              ['top', '⯅ Haut'],
              ['middle', '⇕ Milieu'],
              ['bottom', '⯆ Bas'],
            ].map(([mode, label]) => (
              <button
                key={mode}
                className="sp-btn"
                onClick={() => {
                  const items = selected.filter((i) => !i.locked);
                  if (items.length < 2) return;
                  const xs = items.map((i) => i.x);
                  const ys = items.map((i) => i.y);
                  const xe = items.map((i) => i.x + i.w);
                  const ye = items.map((i) => i.y + i.h);
                  const b = {
                    left: Math.min(...xs),
                    top: Math.min(...ys),
                    right: Math.max(...xe),
                    bottom: Math.max(...ye),
                  };
                  const changes = items.map((it) => {
                    let x = it.x;
                    let y = it.y;
                    if (mode === 'left') x = b.left;
                    if (mode === 'center')
                      x = Math.round((b.left + b.right - it.w) / 2);
                    if (mode === 'right') x = b.right - it.w;
                    if (mode === 'top') y = b.top;
                    if (mode === 'middle')
                      y = Math.round((b.top + b.bottom - it.h) / 2);
                    if (mode === 'bottom') y = b.bottom - it.h;
                    return { id: it.id, changes: { x, y } };
                  });
                  dispatch(studioActions.updateElements(changes));
                  dispatch(studioActions.pushHistory());
                  scheduleSave();
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Document */}
      <div className="sp-section">
        <h3>Document</h3>
        <div className="sp-row">
          <Field label="Largeur (cm)">
            <input
              className="sp-field"
              type="number"
              min="0.1"
              step="0.1"
              value={isNaN(wCm) ? '' : wCm}
              onChange={(e) => {
                const value = num(e.target.value);
                dispatch(
                  studioActions.setDoc({
                    ...doc,
                    widthPx: Math.round(value * CM_TO_PX),
                    widthMm: value * 10,
                  })
                );
                scheduleSave();
              }}
            />
          </Field>
          <Field label="Hauteur (cm)">
            <input
              className="sp-field"
              type="number"
              min="0.1"
              step="0.1"
              value={isNaN(hCm) ? '' : hCm}
              onChange={(e) => {
                const value = num(e.target.value);
                dispatch(
                  studioActions.setDoc({
                    ...doc,
                    heightPx: Math.round(value * CM_TO_PX),
                    heightMm: value * 10,
                  })
                );
                scheduleSave();
              }}
            />
          </Field>
        </div>
        <div className="sp-muted">
          Presets accessibles via &quot;Nouveau document&quot;.
        </div>
      </div>

      {/* Properties */}
      <div className="sp-section">
        <h3>Propriétés</h3>
        {!it && (
          <div className="sp-muted">
            {selected.length === 0
              ? 'Sélectionnez un élément.'
              : `Édition multi-sélection (${selected.length} éléments).`}
          </div>
        )}
        {it && (
          <>
            <div className="sp-row">
              <Field label="X">
                <input
                  className="sp-field"
                  type="number"
                  value={Math.round(it.x || 0)}
                  onChange={(e) => update({ x: num(e.target.value) })}
                />
              </Field>
              <Field label="Y">
                <input
                  className="sp-field"
                  type="number"
                  value={Math.round(it.y || 0)}
                  onChange={(e) => update({ y: num(e.target.value) })}
                />
              </Field>
            </div>
            <div className="sp-row">
              <Field label="Largeur">
                <input
                  className="sp-field"
                  type="number"
                  value={Math.round(it.w || 0)}
                  onChange={(e) => update({ w: num(e.target.value) })}
                />
              </Field>
              <Field label="Hauteur">
                <input
                  className="sp-field"
                  type="number"
                  value={Math.round(it.h || 0)}
                  onChange={(e) => update({ h: num(e.target.value) })}
                />
              </Field>
            </div>
            {it.type !== 'text' &&
              it.type !== 'image' &&
              it.type !== 'line' && (
                <div className="sp-row">
                  <Field label="Rotation (°)">
                    <input
                      className="sp-field"
                      type="number"
                      value={Math.round(it.rotation || 0)}
                      onChange={(e) =>
                        update({ rotation: num(e.target.value) })
                      }
                    />
                  </Field>
                </div>
              )}
            <div className="sp-row">
              {it.type !== 'line' && (
                <ColorPick
                  label="Fond"
                  value={it.fill || 'transparent'}
                  onChange={(v) => update({ fill: v })}
                  allowTransparent={true}
                />
              )}
              <ColorPick
                label="Bordure"
                value={it.stroke || 'transparent'}
                onChange={(v) => update({ stroke: v })}
                allowTransparent={true}
              />
            </div>
            <div className="sp-row">
              <Field label="Largeur bordure">
                <input
                  className="sp-field"
                  type="number"
                  min="0"
                  value={safeNum(it.strokeWidth, 0)}
                  onChange={(e) => update({ strokeWidth: num(e.target.value) })}
                />
              </Field>
              <Field label="Style contour">
                <select
                  className="sp-field"
                  value={it.borderStyle || 'solid'}
                  onChange={(e) => update({ borderStyle: e.target.value })}
                >
                  <option>solid</option>
                  <option>dashed</option>
                  <option>dotted</option>
                  <option>double</option>
                </select>
              </Field>
            </div>
            {it.type !== 'line' && (
              <div>
                <label style={{ marginBottom: 6, display: 'block' }}>
                  Rayon arrondi (px)
                </label>
                <div className="sp-row" style={{ marginBottom: 4 }}>
                  <Field label="↖ Haut gauche">
                    <input
                      className="sp-field"
                      type="number"
                      min="0"
                      value={safeNum(it.radiusTopLeft ?? it.radius, 0)}
                      onChange={(e) =>
                        update({ radiusTopLeft: num(e.target.value) })
                      }
                    />
                  </Field>
                  <Field label="↗ Haut droit">
                    <input
                      className="sp-field"
                      type="number"
                      min="0"
                      value={safeNum(it.radiusTopRight ?? it.radius, 0)}
                      onChange={(e) =>
                        update({ radiusTopRight: num(e.target.value) })
                      }
                    />
                  </Field>
                </div>
                <div className="sp-row">
                  <Field label="↙ Bas gauche">
                    <input
                      className="sp-field"
                      type="number"
                      min="0"
                      value={safeNum(it.radiusBottomLeft ?? it.radius, 0)}
                      onChange={(e) =>
                        update({ radiusBottomLeft: num(e.target.value) })
                      }
                    />
                  </Field>
                  <Field label="↘ Bas droit">
                    <input
                      className="sp-field"
                      type="number"
                      min="0"
                      value={safeNum(it.radiusBottomRight ?? it.radius, 0)}
                      onChange={(e) =>
                        update({ radiusBottomRight: num(e.target.value) })
                      }
                    />
                  </Field>
                </div>
              </div>
            )}
            <div style={{ marginBottom: 8 }}>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  marginBottom: 4,
                }}
              >
                <label>Opacité</label>
                <span className="sp-tiny">
                  {Math.round((it.opacity ?? 1) * 100)}%
                </span>
              </div>
              <input
                type="range"
                className="sp-range-full"
                min="0"
                max="1"
                step="0.01"
                value={safeNum(it.opacity, 1)}
                onChange={(e) => update({ opacity: Number(e.target.value) })}
              />
            </div>

            {/* Text-specific */}
            {it.type === 'text' && (
              <>
                <div style={{ marginBottom: 8 }}>
                  <label>Contenu du texte</label>
                  <textarea
                    className="sp-field"
                    rows="4"
                    value={it.text || ''}
                    onChange={(e) => {
                      // Exit editing mode if editing on canvas, then update text
                      if (it.editing) {
                        dispatch(
                          studioActions.updateElement({
                            id: it.id,
                            changes: { text: e.target.value, editing: false },
                          })
                        );
                        scheduleSave();
                      } else {
                        update({ text: e.target.value });
                      }
                    }}
                    placeholder="Saisissez votre texte..."
                    style={{
                      width: '100%',
                      resize: 'vertical',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div className="sp-row">
                  <ColorPick
                    label="Couleur texte"
                    value={it.color || '#111111'}
                    onChange={(v) => update({ color: v })}
                  />
                  <Field label="Taille">
                    <input
                      className="sp-field"
                      type="number"
                      min="6"
                      value={safeNum(it.fontSize, 24)}
                      onChange={(e) =>
                        update({ fontSize: num(e.target.value) })
                      }
                    />
                  </Field>
                </div>
                <div className="sp-row">
                  <Field label="Font">
                    <select
                      className="sp-field"
                      value={it.fontFamily || 'Marianne'}
                      onChange={(e) => update({ fontFamily: e.target.value })}
                    >
                      {[
                        'Marianne',
                        'Roboto',
                        'Playfair Display',
                        'Inter',
                        'Poppins',
                      ].map((f) => (
                        <option key={f}>{f}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Poids">
                    <select
                      className="sp-field"
                      value={it.fontWeight || '400'}
                      onChange={(e) => update({ fontWeight: e.target.value })}
                    >
                      {['300', '400', '500', '600', '700'].map((w) => (
                        <option key={w}>{w}</option>
                      ))}
                    </select>
                  </Field>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    paddingTop: 8,
                    borderTop: '1px solid var(--sp-line)',
                  }}
                >
                  <label>Lier à un champ DB</label>
                  <div
                    style={{ display: 'flex', gap: 6, alignItems: 'center' }}
                  >
                    <select
                      className="sp-field sp-full"
                      value={
                        it.dbField?.startsWith('{{') &&
                        it.dbField?.endsWith('}}')
                          ? it.dbField.slice(2, -2) // {{db_event_name}} → db_event_name
                          : it.dbField || ''
                      }
                      onChange={(e) => {
                        const value = e.target.value;

                        const cleanedValue = value.startsWith('db_')
                          ? `{{${value}}}` // store as {{db_event_name}}
                          : value;

                        update({ dbField: cleanedValue || null });
                      }}
                    >
                      <option value="">— Aucun —</option>

                      <option value="db_event_name">Event Name</option>
                      <option value="db_product_name">Product Name</option>
                      <option value="db_product_type">Product Type</option>
                      <option value="db_product_description">
                        Product Description
                      </option>
                      <option value="db_exhibitor_name">Exhibitor Name</option>
                      <option value="db_millesime">Millesime</option>
                      <option value="db_region">Region</option>
                      <option value="db_product_price">Product Price</option>
                      <option value="db_hall">Hall</option>
                      <option value="db_stand">Stand</option>
                      <option value="db_cepage">Ce Page</option>
                      <option value="db_type_of_wine">Type Of Wine</option>
                      <option value="db_couleur">Couleur</option>
                      <option value="db_classification">Classification</option>
                      <option value="db_type_culture">Type de culture</option>

                      {(db?.fields || []).map((f) => (
                        <option key={f} value={f}>
                          {f}
                        </option>
                      ))}
                    </select>
                    {it.dbField && (
                      <span className="sp-db-field-tag">🔗 {it.dbField}</span>
                    )}
                  </div>
                  {(!db?.fields || db.fields.length === 0) && (
                    <div className="sp-muted" style={{ marginTop: 4 }}>
                      Aucun champ — ouvrez Base de données.
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </section>
  );
}

RightPanel.propTypes = {
  scheduleSave: PropTypes.func.isRequired,
};
